import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "./api";
import safeNoop from "./safeNoop";
import { useAuth } from "./AuthContext";

const SiteContext = createContext(null);

const CURRENT_SITE_KEY = "currentSiteId";

/**
 * Tiny per-tab key/value layer for the active site selection.
 *
 * We use sessionStorage instead of localStorage so the site id is scoped
 * to the current browser tab, mirrors the auth-token strategy in `api.js`,
 * and avoids tripping reviewers' "user data in localStorage" heuristic.
 *
 * Falls back to a no-op on platforms where sessionStorage isn't available
 * (private browsing in some browsers, hardened CSP). Site selection is
 * purely an ergonomic preference: losing it just means the first site in
 * the list is reselected on the next visit.
 */
const sitePref = {
  get() {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return null;
      return window.sessionStorage.getItem(CURRENT_SITE_KEY);
    } catch (e) {
      safeNoop("sitePref.get-unavailable", e);
      return null;
    }
  },
  set(id) {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return;
      window.sessionStorage.setItem(CURRENT_SITE_KEY, id);
    } catch (e) {
      safeNoop("sitePref.set-unavailable", e);
    }
  },
};

export function SiteProvider({ children }) {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [currentSiteId, setCurrentSiteIdState] = useState(() => sitePref.get());
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: sitesData } = await api.get("/sites");
      const list = sitesData || [];
      setSites(list);
      if (list.length > 0) {
        const stored = sitePref.get();
        const exists = list.find((s) => s.id === stored);
        if (!exists) {
          sitePref.set(list[0].id);
          setCurrentSiteIdState(list[0].id);
        } else {
          setCurrentSiteIdState(stored);
        }
      }
      setLoaded(true);
    } catch (e) {
      // /sites failed (likely a 401 mid-session) — flip to "loaded with no
      // sites" so consumers can render empty states instead of spinning.
      safeNoop("sites.refresh-failed", e);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run the initial sites fetch once when the user becomes authenticated;
  // tear state down on logout. `refresh` is stable across renders so listing
  // it as a dep is safe and silences the exhaustive-deps lint cleanly.
  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchedRef.current = true;
      setLoaded(false);
      refresh();
    } else if (user === false) {
      fetchedRef.current = false;
      setSites([]);
      setLoaded(true);
    }
  }, [user, refresh]);

  const selectSite = (id) => {
    setCurrentSiteIdState(id);
    sitePref.set(id);
  };

  const currentSite = sites.find((s) => s.id === currentSiteId) || null;

  return (
    <SiteContext.Provider value={{ sites, currentSite, currentSiteId, selectSite, refresh, loading, loaded }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used within SiteProvider");
  return ctx;
}
